import { supabase } from '@reader-tracker/shared/lib/supabase'
import type {
  Habit,
  HabitFrequency,
  HabitLog,
  HabitWithLog,
} from '../interfaces'
import {
  computeStreaks,
  getDailyKey,
  getPeriodKey,
  getWeeklyKey,
} from './dateHelpers'
import { normalizeHabitCategory } from './habitCategories'
import { type HabitHeatmapLog, getHabitHeatmapPeriodKeys } from './habitHeatmap'
import { normalizeHabitOrder, sortHabitsByOrder } from './habitOrdering'
import {
  EMPTY_HABIT_DASHBOARD_STATS,
  type HabitDashboardStats,
  type HabitStatsLog,
  calculateHabitDashboardStats,
  getHabitDashboardPeriodKeys,
} from './habitStats'

type HabitLogSummary = Pick<
  HabitLog,
  'id' | 'habit_id' | 'period_key' | 'skipped'
>

interface HabitLogGroups {
  completedKeysByHabit: Map<string, string[]>
  skippedKeysByHabit: Map<string, string[]>
}

interface PeriodLogIndexes {
  completedLogIdByHabitPeriod: Map<string, string>
  skippedLogIdByHabitPeriod: Map<string, string>
}

export type HabitStatsPageHabit = Pick<
  Habit,
  | 'id'
  | 'name'
  | 'category'
  | 'frequency_type'
  | 'frequency_target'
  | 'weekly_days'
  | 'pinned'
  | 'sort_order'
  | 'created_at'
>

export interface HabitStatsPageHeatmap {
  habit: HabitStatsPageHabit
  logs: HabitHeatmapLog[]
}

export interface HabitStatsPageData {
  dashboard: HabitDashboardStats
  heatmaps: HabitStatsPageHeatmap[]
}

export const EMPTY_HABIT_STATS_PAGE_DATA: HabitStatsPageData = {
  dashboard: EMPTY_HABIT_DASHBOARD_STATS,
  heatmaps: [],
}

export function habitCacheKey(
  userId: string,
  date: Date,
  dayStartTime = '00:00',
): string {
  return `habit_cache_${userId}_${getDailyKey(date, dayStartTime)}`
}

export function normalizeHabitRow(row: Habit): Habit {
  const order = normalizeHabitOrder(row.pinned, row.sort_order)

  return {
    ...row,
    category: normalizeHabitCategory(row.category),
    pinned: order.pinned,
    sort_order: order.sort_order,
    frequency_type: row.frequency_type as HabitFrequency,
    weekly_days: row.weekly_days ?? null,
    parent_habit_id: row.parent_habit_id ?? null,
    deeplink_url: row.deeplink_url ?? null,
  }
}

export function createEmptyHabitLog(habit: Habit): HabitWithLog {
  return {
    ...habit,
    isCompletedThisPeriod: false,
    isSkippedThisPeriod: false,
    isWeeklyTargetMet: false,
    logId: null,
    currentStreak: 0,
    bestStreak: 0,
    weeklyCompletions: 0,
  }
}

/**
 * Compute how many times a sub-habit was completed within the current week.
 * Daily and x_per_week sub-habits count each daily log; weekly sub-habits
 * count as 0 or 1 (whether the weekly period was completed).
 */
function computeSubHabitWeeklyCount(
  subHabitId: string,
  subHabitFrequencyType: HabitFrequency,
  currentPeriodLogs: HabitLogSummary[],
  weekDailyKeySet: Set<string>,
  currentWeeklyKey: string,
): number {
  const habitLogs = currentPeriodLogs.filter(
    (l) => l.habit_id === subHabitId && !l.skipped,
  )
  if (subHabitFrequencyType === 'weekly') {
    return habitLogs.some((l) => l.period_key === currentWeeklyKey) ? 1 : 0
  }
  // daily or x_per_week: count daily logs that fall in this week
  return habitLogs.filter((l) => weekDailyKeySet.has(l.period_key)).length
}

/**
 * Derive a parent habit's total weekly contribution from its sub-habits.
 * Each sub-habit completion for the current week adds +1 to the parent count.
 */
export function computeParentWeeklyCount(
  subHabits: HabitWithLog[],
  currentPeriodLogs: HabitLogSummary[],
  weekDailyKeySet: Set<string>,
  currentWeeklyKey: string,
): number {
  return subHabits.reduce(
    (sum, sh) =>
      sum +
      computeSubHabitWeeklyCount(
        sh.id,
        sh.frequency_type,
        currentPeriodLogs,
        weekDailyKeySet,
        currentWeeklyKey,
      ),
    0,
  )
}

export async function loadHabitsWithLogs(
  userId: string,
  date: Date,
  dayStartTime: string,
): Promise<HabitWithLog[]> {
  const { data } = await supabase
    .schema('habit')
    .from('habits')
    .select('*')
    .eq('user_id', userId)
    .eq('archived', false)
    .order('pinned', { ascending: false })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true })

  const allHabitRows = ((data ?? []) as Habit[]).map(normalizeHabitRow)

  // Separate top-level habits from sub-habits
  const topLevelRows = allHabitRows.filter((h) => h.parent_habit_id === null)
  const subHabitRows = allHabitRows.filter((h) => h.parent_habit_id !== null)

  const { week } = getHabitDashboardPeriodKeys(date, dayStartTime)
  const weekDailyKeySet = new Set(week.dailyKeys)
  const currentWeeklyKey = getWeeklyKey(date, dayStartTime)
  const selectedPeriodKeys = [
    ...new Set([
      getDailyKey(date, dayStartTime),
      currentWeeklyKey,
      ...week.dailyKeys,
    ]),
  ]

  const { allLogs, currentPeriodLogs } = await fetchHabitLogs(
    allHabitRows.map((h) => h.id),
    selectedPeriodKeys,
    userId,
  )

  const logGroups = groupHabitLogs(allLogs)
  const periodLogIndexes = indexPeriodLogs(currentPeriodLogs)
  const weeklyCompletionsByHabit = countWeeklyCompletions(
    currentPeriodLogs,
    weekDailyKeySet,
  )

  // Compute HabitWithLog for every habit (top-level and sub-habits alike)
  const habitWithLogById = new Map<string, HabitWithLog>()
  for (const habit of allHabitRows) {
    habitWithLogById.set(
      habit.id,
      toHabitWithLog(
        habit,
        date,
        dayStartTime,
        logGroups,
        periodLogIndexes,
        weeklyCompletionsByHabit,
      ),
    )
  }

  // Group sub-habits by their parent id
  const subHabitsByParentId = new Map<string, HabitWithLog[]>()
  for (const subHabit of subHabitRows) {
    const parentId = subHabit.parent_habit_id as string
    const hwl = habitWithLogById.get(subHabit.id) as HabitWithLog
    const list = subHabitsByParentId.get(parentId) ?? []
    list.push(hwl)
    subHabitsByParentId.set(parentId, list)
  }

  // Build final top-level list, injecting derived parent progress where needed
  const result = topLevelRows.map((habit) => {
    const hwl = habitWithLogById.get(habit.id) as HabitWithLog
    const subHabits = subHabitsByParentId.get(habit.id)

    if (!subHabits || subHabits.length === 0) return hwl

    // Parent's completion count = sum of sub-habit contributions this week
    const parentWeeklyCount = computeParentWeeklyCount(
      subHabits,
      currentPeriodLogs,
      weekDailyKeySet,
      currentWeeklyKey,
    )
    const parentTarget = habit.frequency_target ?? 1
    const isParentComplete = parentWeeklyCount >= parentTarget

    const isParentDoneThisPeriod =
      subHabits.length > 0 &&
      subHabits.every((sh) => sh.logId !== null && !sh.isSkippedThisPeriod)

    return {
      ...hwl,
      subHabits,
      weeklyCompletions: parentWeeklyCount,
      isCompletedThisPeriod: isParentDoneThisPeriod,
      isWeeklyTargetMet: isParentComplete,
      // Streak for parents-with-sub-habits is not yet tracked (v1)
      currentStreak: 0,
      bestStreak: 0,
    }
  })

  return sortHabitsByOrder(result)
}

export async function loadNextHabitSortOrder(userId: string): Promise<number> {
  const { data } = await supabase
    .schema('habit')
    .from('habits')
    .select('sort_order')
    .eq('user_id', userId)
    .is('parent_habit_id', null)
    .order('sort_order', { ascending: false })
    .limit(1)

  return ((data?.[0]?.sort_order as number | undefined) ?? -1) + 1
}

export async function loadHabitDashboardStats(
  userId: string,
  selectedDate: Date,
  dayStartTime: string,
): Promise<HabitDashboardStats> {
  const { data: habitRows } = await supabase
    .schema('habit')
    .from('habits')
    .select('id, frequency_type, frequency_target, weekly_days')
    .eq('user_id', userId)
    .eq('archived', false)
    // Exclude sub-habits from dashboard stats (counted through their parent)
    .is('parent_habit_id', null)

  const habits = (
    (habitRows ?? []) as Pick<
      Habit,
      'id' | 'frequency_type' | 'frequency_target' | 'weekly_days'
    >[]
  ).map((habit) => ({
    id: habit.id,
    frequency_type: habit.frequency_type,
    frequency_target: habit.frequency_target,
    weekly_days: habit.weekly_days,
  }))

  if (habits.length === 0) {
    return EMPTY_HABIT_DASHBOARD_STATS
  }

  const periodKeys = getHabitDashboardPeriodKeys(selectedDate, dayStartTime)
  const selectedPeriodKeys = [
    ...new Set([
      ...periodKeys.week.dailyKeys,
      ...periodKeys.week.weeklyKeys,
      ...periodKeys.month.dailyKeys,
      ...periodKeys.month.weeklyKeys,
    ]),
  ]

  const { data: logRows } = await supabase
    .schema('habit')
    .from('habit_logs')
    .select('habit_id, period_key, skipped')
    .eq('user_id', userId)
    .in(
      'habit_id',
      habits.map((habit) => habit.id),
    )
    .in('period_key', selectedPeriodKeys)

  return calculateHabitDashboardStats(
    habits,
    (logRows ?? []) as HabitStatsLog[],
    selectedDate,
    dayStartTime,
  )
}

export async function loadHabitStatsPageData(
  userId: string,
  selectedDate: Date,
  dayStartTime: string,
): Promise<HabitStatsPageData> {
  const { data: habitRows } = await supabase
    .schema('habit')
    .from('habits')
    .select(
      'id, name, category, frequency_type, frequency_target, weekly_days, pinned, sort_order, created_at',
    )
    .eq('user_id', userId)
    .eq('archived', false)
    .is('parent_habit_id', null)
    .order('pinned', { ascending: false })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true })

  const habits = sortHabitsByOrder(
    ((habitRows ?? []) as HabitStatsPageHabit[]).map((habit) => ({
      ...habit,
      category: normalizeHabitCategory(habit.category),
      frequency_type: habit.frequency_type as HabitFrequency,
      ...normalizeHabitOrder(habit.pinned, habit.sort_order),
    })),
  )

  if (habits.length === 0) {
    return EMPTY_HABIT_STATS_PAGE_DATA
  }

  const dashboardPeriodKeys = getHabitDashboardPeriodKeys(
    selectedDate,
    dayStartTime,
  )
  const heatmapPeriodKeys = getHabitHeatmapPeriodKeys(
    selectedDate,
    dayStartTime,
  )
  const selectedPeriodKeys = [
    ...new Set([
      ...dashboardPeriodKeys.week.dailyKeys,
      ...dashboardPeriodKeys.week.weeklyKeys,
      ...dashboardPeriodKeys.month.dailyKeys,
      ...dashboardPeriodKeys.month.weeklyKeys,
      ...heatmapPeriodKeys,
    ]),
  ]

  const { data: logRows } = await supabase
    .schema('habit')
    .from('habit_logs')
    .select('habit_id, period_key, skipped')
    .eq('user_id', userId)
    .in(
      'habit_id',
      habits.map((habit) => habit.id),
    )
    .in('period_key', selectedPeriodKeys)

  const logs = (logRows ?? []) as HabitStatsLog[]
  const heatmapPeriodKeySet = new Set(heatmapPeriodKeys)
  const heatmapLogsByHabitId = new Map<string, HabitHeatmapLog[]>()

  for (const log of logs) {
    if (!heatmapPeriodKeySet.has(log.period_key)) continue

    const habitLogs = heatmapLogsByHabitId.get(log.habit_id) ?? []
    habitLogs.push({
      period_key: log.period_key,
      skipped: log.skipped,
    })
    heatmapLogsByHabitId.set(log.habit_id, habitLogs)
  }

  return {
    dashboard: calculateHabitDashboardStats(
      habits,
      logs,
      selectedDate,
      dayStartTime,
    ),
    heatmaps: habits.map((habit) => ({
      habit,
      logs: heatmapLogsByHabitId.get(habit.id) ?? [],
    })),
  }
}

async function fetchHabitLogs(
  habitIds: string[],
  selectedPeriodKeys: string[],
  userId?: string,
): Promise<{
  allLogs: HabitLogSummary[]
  currentPeriodLogs: HabitLogSummary[]
}> {
  if (habitIds.length === 0) {
    return { allLogs: [], currentPeriodLogs: [] }
  }

  let allLogsQuery = supabase
    .schema('habit')
    .from('habit_logs')
    .select('id, habit_id, period_key, skipped')
    .in('habit_id', habitIds)

  let currentPeriodQuery = supabase
    .schema('habit')
    .from('habit_logs')
    .select('id, habit_id, period_key, skipped')
    .in('habit_id', habitIds)
    .in('period_key', selectedPeriodKeys)

  if (userId) {
    allLogsQuery = allLogsQuery.eq('user_id', userId)
    currentPeriodQuery = currentPeriodQuery.eq('user_id', userId)
  }

  const [allLogsResult, currentPeriodResult] = await Promise.all([
    allLogsQuery,
    currentPeriodQuery,
  ])

  return {
    allLogs: (allLogsResult.data ?? []) as HabitLogSummary[],
    currentPeriodLogs: (currentPeriodResult.data ?? []) as HabitLogSummary[],
  }
}

function appendPeriodKey(
  map: Map<string, string[]>,
  habitId: string,
  periodKey: string,
): void {
  const keys = map.get(habitId) ?? []
  keys.push(periodKey)
  map.set(habitId, keys)
}

function groupHabitLogs(logs: HabitLogSummary[]): HabitLogGroups {
  const completedKeysByHabit = new Map<string, string[]>()
  const skippedKeysByHabit = new Map<string, string[]>()

  for (const log of logs) {
    appendPeriodKey(
      log.skipped ? skippedKeysByHabit : completedKeysByHabit,
      log.habit_id,
      log.period_key,
    )
  }

  return { completedKeysByHabit, skippedKeysByHabit }
}

function indexPeriodLogs(logs: HabitLogSummary[]): PeriodLogIndexes {
  const completedLogIdByHabitPeriod = new Map<string, string>()
  const skippedLogIdByHabitPeriod = new Map<string, string>()

  for (const log of logs) {
    const key = `${log.habit_id}:${log.period_key}`
    if (log.skipped) {
      skippedLogIdByHabitPeriod.set(key, log.id)
    } else {
      completedLogIdByHabitPeriod.set(key, log.id)
    }
  }

  return { completedLogIdByHabitPeriod, skippedLogIdByHabitPeriod }
}

function countWeeklyCompletions(
  logs: HabitLogSummary[],
  weekDailyKeySet: Set<string>,
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const log of logs) {
    if (log.skipped) continue
    if (!weekDailyKeySet.has(log.period_key)) continue
    counts.set(log.habit_id, (counts.get(log.habit_id) ?? 0) + 1)
  }
  return counts
}

function toHabitWithLog(
  habit: Habit,
  date: Date,
  dayStartTime: string,
  logGroups: HabitLogGroups,
  periodLogIndexes: PeriodLogIndexes,
  weeklyCompletionsByHabit: Map<string, number>,
): HabitWithLog {
  const periodKey = getPeriodKey(habit.frequency_type, date, dayStartTime)
  const mapKey = `${habit.id}:${periodKey}`
  const completedLogId =
    periodLogIndexes.completedLogIdByHabitPeriod.get(mapKey) ?? null
  const skippedLogId =
    periodLogIndexes.skippedLogIdByHabitPeriod.get(mapKey) ?? null
  const completedKeys = logGroups.completedKeysByHabit.get(habit.id) ?? []
  const skippedKeys = logGroups.skippedKeysByHabit.get(habit.id) ?? []
  const { current, best } = computeStreaks(
    completedKeys,
    habit.frequency_type,
    habit.frequency_target ?? 1,
    skippedKeys,
    dayStartTime,
    habit.weekly_days,
  )

  // weeklyCompletions is always computed (for all habits) so sub-habit
  // contributions can be summed up by their parent.
  const weeklyCompletions = weeklyCompletionsByHabit.get(habit.id) ?? 0

  const isWeeklyTargetMet =
    habit.frequency_type === 'x_per_week' &&
    weeklyCompletions >= (habit.frequency_target ?? 1)

  return {
    ...habit,
    isCompletedThisPeriod: completedLogId !== null,
    isSkippedThisPeriod: skippedLogId !== null,
    isWeeklyTargetMet,
    logId: completedLogId ?? skippedLogId,
    currentStreak: current,
    bestStreak: best,
    weeklyCompletions,
  }
}
